precision highp float;

in vec3 position; // we only use the x coordinate, but three.js requires a vec3


// Latest GPGPU pass: encoded particle positions (RGBA packed coords + parity bit in R).
uniform sampler2D rttTexture;
// Second MRT: normalized scalar field magnitude (R); decoded in VS as fieldScalar.
uniform sampler2D rttScalarTexture;
// Snapshot of positions at RTT tick start; used with rttTexture to compute motion `speed`.
uniform sampler2D rttTexturePrev;
// Width/height of the particle state texture in texels (`maxAmount`); maps vertex index -> UV.
uniform float rttSize;
// Screen size in pixels.
uniform float screenSize;
// Map canvas area in px^2 (`width * height`); with `PARTICLE_DENSITY` caps how many particle
// indices are drawn (see vertex shader density test).
uniform float particleDensityArea;

// Frame interval in seconds; multiplies position delta for motion `speed`.
uniform float renderStepSize;

// When true, point size scales slightly with motion `speed` (vertex shader).
uniform bool fastIsLarger;

// Screen-space motion approx px/s between RTT frames.
out float speed;
// Decoded field magnitude at particle: rttScalar R x PARTICLE_FIELD_MAGNITUDE_MAX.
out float fieldScalar;

float decodeFracX(float r) {
    float r255 = floor(r * 255.0);
    float r255Even = r255 - mod(r255, 2.0);
    return r255Even / 255.0;
}

vec4 advance(float position_) {
    // If this particle exceeds the density threshold (i.e., not used), output zero speed and dummy data.
    if (position_ > (PARTICLE_DENSITY / 1000.0) * particleDensityArea) {
        speed = 0.0;
        fieldScalar = 0.0;
        return vec4(0.0);
    }

    // Exact texel lookup for this particle index.
    int ix = int(mod(position_, rttSize));
    int iy = int(floor(position_ / rttSize));
    ivec2 texel = ivec2(ix, iy);

    // Read encoded particle data from current RTT: color.ba = coarse coordinates, color.rg = finer offsets.
    vec4 color = texelFetch(rttTexture, texel, 0);

    // Combine for precise position, this is a hack
    // ba contains the integer part of the position
    // rg contains the fractional part of the position
    vec2 pos = color.ba + vec2(decodeFracX(color.r), color.g) / 255.0;

    // Do the same for previous frame texture, to compute movement.
    vec4 colorPrev = texelFetch(rttTexturePrev, texel, 0);
    vec2 posPrev = colorPrev.ba + vec2(decodeFracX(colorPrev.r), colorPrev.g) / 255.0;

    // Compute vector difference (motion) between frames.
    vec2 diff = pos - posPrev;

    // Calculate speed as a function of frame time and moved distance, in screen units.
    // Protects against division by zero when particle is stationary.
    speed = (renderStepSize * length(diff * screenSize));

    vec4 scalarTexel = texelFetch(rttScalarTexture, texel, 0);
    float scalarNorm = scalarTexel.r;
    fieldScalar = scalarNorm * PARTICLE_FIELD_MAGNITUDE_MAX;

    // Preferred line direction comes from RTT advection field (packed in GB), fallback to frame diff.
    vec2 flowDir = scalarTexel.gb * 2.0 - 1.0;
    float flowLen = length(flowDir);
    vec2 lineDir = flowLen > 0.0 ? flowDir / flowLen : diff;

    // Return current position + direction vector for segment orientation.
    return vec4(pos, lineDir);
}

void main() {
  // Two vertices are emitted per particle line segment; map vertex index -> particle index.
  float position_ = floor(position.x / 2.0);

  vec4 posdiff = advance(position_);
  // Signed endpoint selector: -1 and +1.
  float endpoint = 4.0 * fract(position.x / 2.0) - 1.0;
  vec2 pos = posdiff.xy;
  vec2 diff = posdiff.zw;
  float diffLen = length(diff);
  vec2 dir = diffLen > 0.0 ? diff / diffLen : vec2(0.0);

  // Extend the segment along motion direction.
  float size = fastIsLarger 
    ? (PARTICLE_SIZE + PARTICLE_SIZE * log(speed + 1.0)) / 2.0
    : PARTICLE_SIZE;

  vec2 uv = (float(size) / screenSize) * endpoint * dir;

  pos = 2.0 * (pos - uv) - 1.0;
  gl_Position = vec4(pos.x, -pos.y, 0., 1.);
}
